import { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { identifyUser, setUserProperties } from './utils/analytics';
import { useIsPremium } from './utils/useIsPremium';
import {
  useFlutterDeepLink,
  getDeepLinkParams,
  type FlutterDeepLinkParams,
} from './components/useFlutterDeepLink';
import { useTranslation } from 'react-i18next';
import './i18n';
import { logDebugModeStatus } from './utils/firebaseFunctions';
import UserHeader from './components/UserHeader';
import SpinnerOverlay from './components/SpinnerOverlay';
import { setDeepLinkData, getDeepLinkData } from './utils/deeplinkStorage';
import { getLoggedInUserId, saveAffiliateToFirestore } from './utils/affiliatesFirestore';
import { syncFcmUserId } from './utils/fcmSync';
import { hasPassedOnboarding } from './utils/userSelections';

// Route pages are lazy-loaded (one chunk per page instead of one monolithic
// bundle) so the initial load only ships the code the current route needs.
const LoginPage = lazy(() => import('./components/LoginPage'));
const AppOnboardingPage = lazy(() => import('./pages/AppOnboardingPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const BirthDatePage = lazy(() => import('./pages/BirthDatePage'));
const ChooseRabbiPage = lazy(() => import('./pages/ChooseRabbiPage'));
const PracticePage = lazy(() => import('./pages/PracticePage'));
const ProgressPage = lazy(() => import('./pages/ProgressPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AccuracyResultPage = lazy(() => import('./pages/AccuracyResultPage'));
const RabbiUploadPage = lazy(() => import('./pages/RabbiUploadPage'));
const RabbiLoginPage = lazy(() => import('./pages/RabbiLoginPage'));
const RabbiPortalPage = lazy(() => import('./pages/RabbiPortalPage'));
const ChooseParshaPage = lazy(() => import('./pages/ChooseParshaPage'));
const RegisterRabbiRequestPage = lazy(() => import('./pages/RegisterRabbiRequestPage'));
const UserPortalPage = lazy(() => import('./pages/UserPortalPage'));
const ShemaPage = lazy(() => import('./pages/ShemaPage'));
const BirchotHatorahPage = lazy(() => import('./pages/BirchotHatorahPage'));
const ChooseDatePage = lazy(() => import('./pages/ChooseDatePage'));
const CompareResultsPage = lazy(() => import('./pages/CompareResultsPage'));
const StudentDetailsPage = lazy(() => import('./pages/StudentDetailsPage'));
const PdfViewPage = lazy(() => import('./pages/PdfViewPage'));
const TikunKorimPage = lazy(() => import('./pages/TikunKorimPage'));
const PaymentsPage = lazy(() => import('./pages/PaymentsPage'));
const PaymentsPageV2 = lazy(() => import('./pages/PaymentsPageV2'));
const LanguagePage = lazy(() => import('./pages/LanguagePage'));
const MyProgressPage = lazy(() => import('./pages/MyProgressPage'));
const ParentProgressPage = lazy(() => import('./pages/ParentProgressPage'));
const ConnectParentPage = lazy(() => import('./pages/ConnectParentPage'));
const ConnectChildPage = lazy(() => import('./pages/ConnectChildPage'));
const YamenPracticePage = lazy(() => import('./pages/YamenPracticePage'));
const TraditionPage = lazy(() => import('./pages/TraditionPage'));
const ConnectRabbiPage = lazy(() => import('./pages/ConnectRabbiPage'));
const ChooseReadingsPage = lazy(() => import('./pages/ChooseReadingsPage'));
const ChooseParentChildPage = lazy(() => import('./pages/ChooseParentChildPage'));
const ParashaOverviewPage = lazy(() => import('./pages/ParashaOverviewPage'));
const TriviaQuizPage = lazy(() => import('./pages/TriviaQuizPage'));
const TriviaResultPage = lazy(() => import('./pages/TriviaResultPage'));
const TriviaChallengesPage = lazy(() => import('./pages/TriviaChallengesPage'));
const GetAppPage = lazy(() => import('./pages/GetAppPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const ContactUsPage = lazy(() => import('./pages/ContactUsPage'));

function AppContent() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // React to deep links while app is open (Flutter WebView)
  const onDeepLink = useCallback(
    ({ path, queryParameters }: FlutterDeepLinkParams) => {
      if (path) {
        const search =
          queryParameters && Object.keys(queryParameters).length > 0
            ? `?${new URLSearchParams(queryParameters).toString()}`
            : '';
        navigate(path + search, { replace: false });
      }
    },
    [navigate],
  );
  useFlutterDeepLink(onDeepLink);

  // React to push notification taps (Flutter dispatches 'flutterNotificationNav' event)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ path?: string }>).detail;
      if (detail?.path) {
        navigate(detail.path, { replace: false });
      }
    };
    window.addEventListener('flutterNotificationNav', handler);
    return () => window.removeEventListener('flutterNotificationNav', handler);
  }, [navigate]);

  // Persist deep link data: from location; preserve existing query params (e.g. aid) when current URL has none (e.g. after navigating to /settings)
  // If deeplink has "aid", save userId + affiliateId to Firestore "affiliates" collection
  useEffect(() => {
    const fromLocation: Record<string, string> = {};
    new URLSearchParams(location.search).forEach((value, key) => {
      fromLocation[key] = value;
    });

    // When current URL has no query params, keep existing stored params (e.g. aid from initial deeplink) so we don't overwrite with empty
    const queryParameters =
      Object.keys(fromLocation).length > 0
        ? fromLocation
        : (getDeepLinkData()?.queryParameters ?? fromLocation);

    const applyDeeplink = (path: string, queryParams: Record<string, string>) => {
      const search =
        Object.keys(queryParams).length > 0
          ? `?${new URLSearchParams(queryParams).toString()}`
          : '';
      const deeplinkInfo = { path, search, queryParameters: queryParams };
      console.log('[Deep link] React received:', deeplinkInfo);
      setDeepLinkData(deeplinkInfo);

      const affiliateId = queryParams.aid ?? null;
      if (affiliateId != null && affiliateId.trim() !== '') {
        const userId = getLoggedInUserId();
        if (userId) {
          saveAffiliateToFirestore(userId, affiliateId.trim()).catch((err) =>
            console.warn('[Deep link] Failed to save affiliate:', err),
          );
        }
      }
    };

    applyDeeplink(location.pathname, queryParameters);
  }, [location.pathname, location.search]);

  // In Flutter WebView, get initial deeplink params from native so we don't lose aid when the loaded URL was redirected (e.g. to /aliya-latora)
  useEffect(() => {
    const win =
      typeof window !== 'undefined'
        ? (window as Window & {
            flutter_inappwebview?: {
              callHandler: (name: string) => Promise<FlutterDeepLinkParams>;
            };
          })
        : null;
    if (!win?.flutter_inappwebview) return;

    getDeepLinkParams()
      .then((params) => {
        if (!params?.queryParameters || Object.keys(params.queryParameters).length === 0) return;
        const path = params.path || location.pathname;
        const queryParameters = params.queryParameters as Record<string, string>;
        const deeplinkInfo = {
          path,
          search: `?${new URLSearchParams(queryParameters).toString()}`,
          queryParameters,
        };
        console.log('[Deep link] React (from Flutter):', deeplinkInfo);
        setDeepLinkData(deeplinkInfo);

        const affiliateId = queryParameters.aid ?? null;
        if (affiliateId != null && affiliateId.trim() !== '') {
          const userId = getLoggedInUserId();
          if (userId) {
            saveAffiliateToFirestore(userId, affiliateId.trim()).catch((err) =>
              console.warn('[Deep link] Failed to save affiliate:', err),
            );
          }
        }
      })
      .catch(() => {});
    // Run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After language-change reload: redirect from /choose-language back to the page user came from
  useEffect(() => {
    if (location.pathname === '/choose-language') {
      const returnPath = sessionStorage.getItem('i18n_return_path');
      if (returnPath && returnPath !== '/choose-language') {
        sessionStorage.removeItem('i18n_return_path');
        navigate(returnPath, { replace: true });
      }
    }
  }, [location.pathname, navigate]);

  // Log debug mode status on app startup
  useEffect(() => {
    logDebugModeStatus();
  }, []);

  // When opened from Flutter WebView with ?payments=true, persist so the menu shows the payments item for the whole session
  useEffect(() => {
    // if(localStorage.getItem("showPayments") === null) {
    //     const params = new URLSearchParams(window.location.search);
    //     if (params.get("payments") === "true") {
    //         localStorage.setItem("showPayments", "true");
    //     } else {
    //         localStorage.setItem("showPayments", "false");
    //     }
    // }
    localStorage.setItem('showPayments', 'true');
  }, []);

  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('loggedInUser');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  // Re-identify the user with Mixpanel on every app startup so events from returning
  // users are not attributed to anonymous IDs (Mixpanel identity is stored in its own
  // storage and can be cleared independently of our loggedInUser in localStorage).
  useEffect(() => {
    const raw = localStorage.getItem('loggedInUser');
    if (!raw) return;
    const stored = JSON.parse(raw);
    const isGuest = String(stored.userId || '').startsWith('guest-');
    if (isGuest) return;
    const mixpanelId = stored.email || stored.userId;
    if (mixpanelId) {
      identifyUser(mixpanelId, { name: stored.name ?? stored.displayName, email: stored.email });
    }
  }, []);

  // Same guest/mixpanelId gating as the identify effect above, so the `premium`
  // profile property is only pushed for users we actually identified with Mixpanel.
  const isGuestUser = String(user?.userId || '').startsWith('guest-');
  const mixpanelId = !isGuestUser ? user?.email || user?.userId : null;
  const { isPremium, loading: premiumLoading } = useIsPremium(
    !isGuestUser ? (user?.email ?? null) : null,
  );

  // Keep the Mixpanel `premium` user property in sync with subscription/whitelist
  // status. Fires whenever the underlying Firestore listeners resolve or change
  // (login, purchase, whitelist edit, or expiry noticed on next app open).
  useEffect(() => {
    if (!mixpanelId || premiumLoading) return;
    setUserProperties({ premium: isPremium });
  }, [mixpanelId, isPremium, premiumLoading]);

  // Link the logged-in userId to the current FCM token so notifications can be targeted.
  // Runs once on mount and whenever the user changes; retries internally if Flutter's
  // token isn't ready yet.
  useEffect(() => {
    const userId = getLoggedInUserId();
    if (userId) {
      syncFcmUserId(userId).catch(() => {});
    }
  }, [user]);

  // Hide top bar (UserHeader with hamburger) only on UserPortalPage
  const pagesWithoutHeader = [
    'tradition',
    'connect-rabbi',
    'choose-readings',
    'aliya-latora',
    'choose-parent-child',
    'user-portal',
    'onboarding',
    '',
    'my-progress',
    'parent-progress',
    'connect-child',
    'connect-parent',
    'parasha-overview',
    'trivia-result',
    'trivia-quiz',
    'trivia-challenges',
    'get-app',
    'practice',
  ];
  const currentLocation = location.pathname.replaceAll('/', '').toLowerCase();
  const showUserHeader = !pagesWithoutHeader.some((path) => path.includes(currentLocation));

  // Decide the "/" destination before the first paint so returning users never
  // see a flash of the onboarding carousel while a post-mount effect redirects them.
  const rootIsUserPortal = hasPassedOnboarding() && !!localStorage.getItem('loggedInUser');

  return (
    // <GoogleOAuthProvider clientId="534754909123-neqev2vil450jcrksnv4lasaf797mign.apps.googleusercontent.com">
    <div className="h-screen w-full flex flex-col bg-[#F9FAFB] overflow-hidden">
      {/* Fixed header at top */}
      {showUserHeader && <UserHeader />}
      {/* Scrollable content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <Suspense fallback={<SpinnerOverlay message={t('loading', 'טוען...') ?? undefined} />}>
          <Routes>
            {/* App onboarding (first-time user intro) - main entry point.
                Returning users who already passed onboarding go straight to
                the user portal, decided synchronously above so there's no
                render of the carousel before the redirect. */}
            <Route
              path="/"
              element={rootIsUserPortal ? <UserPortalPage /> : <AppOnboardingPage />}
            />
            {/* Login/signup onboarding */}
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/choose-parent-child" element={<ChooseParentChildPage />} />
            {/* <Route path="/aliya-latora" element={<AliyaLatoraPage />} /> */}
            <Route path="/birthdate" element={<BirthDatePage />} />
            <Route path="/connect-rabbi" element={<ConnectRabbiPage />} />
            <Route path="/tradition" element={<TraditionPage />} />
            <Route path="/choose-readings" element={<ChooseReadingsPage />} />
            <Route path="/aliya-latora" element={<ChooseDatePage />} />
            <Route path="/yamen-practice" element={<YamenPracticePage />} />
            <Route path="/choose-parasha" element={<ChooseParshaPage />} />
            <Route path={'/chooserabbi'} element={<ChooseRabbiPage />} />
            <Route path={'/practice'} element={<PracticePage />} />
            <Route path={'/parasha-overview'} element={<ParashaOverviewPage />} />
            <Route path={'/progress'} element={<ProgressPage />} />
            <Route path={'/user-portal'} element={<UserPortalPage />} />
            <Route path={'/shema'} element={<ShemaPage />} />
            <Route path={'/birchot-hatorah'} element={<BirchotHatorahPage />} />
            <Route path={'/settings'} element={<SettingsPage />} />
            <Route path={'/accuracy-result'} element={<AccuracyResultPage />} />
            <Route path={'/compare-results'} element={<CompareResultsPage />} />
            <Route path={'/rabbi'} element={<RabbiPortalPage />} />
            <Route path={'/rabbi-upload'} element={<RabbiUploadPage />} />
            <Route path={'/rabbi-login'} element={<RabbiLoginPage />} />
            <Route path={'/terms-of-use'} element={<PdfViewPage />} />
            <Route path={'/tikun-korim'} element={<TikunKorimPage />} />
            <Route path={'/payments'} element={<PaymentsPageV2 />} />
            <Route path={'/payments-classic'} element={<PaymentsPage />} />
            <Route path={'/choose-language'} element={<LanguagePage />} />
            <Route path={'/my-progress'} element={<MyProgressPage />} />
            <Route path={'/parent-progress'} element={<ParentProgressPage />} />
            <Route path={'/connect-parent'} element={<ConnectParentPage />} />
            <Route path={'/connect-child'} element={<ConnectChildPage />} />
            <Route path={'/trivia-quiz'} element={<TriviaQuizPage />} />
            <Route path={'/trivia-result'} element={<TriviaResultPage />} />
            <Route path={'/trivia-challenges'} element={<TriviaChallengesPage />} />
            <Route path={'/get-app'} element={<GetAppPage />} />
            <Route path={'/faq'} element={<FAQPage />} />
            <Route path={'/contact-us'} element={<ContactUsPage />} />
            <Route path={'/register-rabbi-request'} element={<RegisterRabbiRequestPage />} />
            <Route path={'/student-details/:studentId'} element={<StudentDetailsPage />} />
            {/* Existing login route unchanged */}
            <Route
              path="/login"
              element={
                user ? (
                  <div className="flex flex-col items-center justify-center min-h-screen">
                    <div className="bg-white p-8 rounded shadow-md w-full max-w-sm text-center">
                      <h2 className="text-2xl font-bold mb-6">
                        {t(
                          'already_logged_in',
                          '\u05db\u05d1\u05e8 \u05d4\u05ea\u05d7\u05d1\u05e8\u05ea',
                        )}
                      </h2>
                      <button
                        className="mt-4 bg-red-500 text-white px-4 py-2 rounded-xl"
                        onClick={() => {
                          localStorage.removeItem('loggedInUser');
                          setUser(null);
                          navigate('/onboarding', { replace: true });
                        }}
                      >
                        {t('logout', '\u05d4\u05ea\u05e0\u05ea\u05e7')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <LoginPage />
                )
              }
            />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AppContent />
    </Router>
  );
}

export default App;
